/**
 * Il flusso di avvio con duplicati e conferma (CF09/10) — CL06.
 *
 * La regola che questo modulo esiste per proteggere è **negativa**: non dire
 * all'utente che il processo è partito finché non è partito.
 *
 * `prepare` riserva un numero e non fa niente di BPM. `prepared` significa che
 * *potrebbe* partire. Solo `applied` significa che è partito. Un'interfaccia che
 * mostrasse "avviato" su `prepared` direbbe una cosa vera a metà — e l'utente lo
 * scoprirebbe ricaricando la pagina.
 *
 * Le altre regole, e perché:
 *
 * - **Un `operationId` per intento, e l'originale non si riscrive.** Ritentare
 *   con lo stesso id è ciò che rende il retry sicuro: il server riconosce
 *   l'operazione e non riapplica. Un id nuovo è un secondo avvio.
 * - **`CONFIRMATION_STALE` riapre una conferma nuova**, non riusa quella
 *   scaduta: il consenso era per un'analisi che non vale più.
 * - **Annullare non consuma il numero.** Il numero si riserva al prepare, quindi
 *   annullare *dopo* il prepare lo consuma per davvero — ma annullare prima di
 *   confermare non deve diventare un avvio a metà.
 * - **Un'origine automatica rifiutata è una notifica**, mai una richiesta di
 *   approvazione: un timer non ha nessuno che possa approvare.
 */

import type { OperationOrigin, OperationStatus } from "model/CustomFields";

/**
 * Cosa può dire l'interfaccia, dedotto dallo stato dell'operazione.
 *
 * Il tipo è deliberatamente povero: non c'è un modo di scrivere "avviato" senza
 * `applied`.
 */
export type StartOutcome =
  | { kind: "idle" }
  | { kind: "working"; stage: "preparing" | "confirming" }
  /** C'è una scelta da fare: dei codici sono già usati. */
  | { kind: "needs_confirmation"; codes: string[]; anomalies: number }
  /** Non partito, e si può ritentare con lo stesso intento. */
  | { kind: "refused"; code: string; codes?: string[] }
  /** Partito. Solo qui. */
  | { kind: "started"; resultPreview: Record<string, string | boolean | string[] | null> | null }
  /** Non si sa ancora: si consulta l'esito, non si riprepara. */
  | { kind: "unknown"; operationId: string };

/**
 * Da uno stato dell'operazione all'esito mostrabile.
 *
 * `applied` è l'unico che produce `started`. `prepared` e `validated` sono
 * *in corso*, non *fatti*: la differenza fra "il numero è riservato" e "il
 * processo esiste" è tutta la differenza.
 */
export function outcomeOfStatus(
  status: OperationStatus,
  resultPreview: Record<string, string | boolean | string[] | null> | null = null
): StartOutcome {
  switch (status) {
    case "validated":
    case "prepared":
      // Il numero è riservato e niente di BPM è avvenuto. Dire "avviato" qui
      // sarebbe una promessa che il motore non ha ancora mantenuto.
      return { kind: "working", stage: "preparing" };
    case "awaiting_confirmation":
      return { kind: "needs_confirmation", codes: [], anomalies: 0 };
    case "applied":
      return { kind: "started", resultPreview };
    case "rejected":
      return { kind: "refused", code: "DUPLICATE_CODE_REJECTED" };
    case "expired":
      return { kind: "refused", code: "OPERATION_EXPIRED" };
    default:
      return { kind: "idle" };
  }
}

/** Gli stati da cui non si esce: interrogare ancora non ha senso. */
export const isTerminal = (status: OperationStatus): boolean =>
  status === "applied" || status === "rejected" || status === "expired";

/**
 * L'interfaccia può inviare?
 *
 * Guardia contro il doppio submit: due clic ravvicinati produrrebbero due
 * `prepare` con lo stesso intento, e il secondo è un conflitto — che l'utente
 * leggerebbe come un errore mentre è solo impazienza.
 */
export const canSubmit = (outcome: StartOutcome): boolean =>
  outcome.kind === "idle" || outcome.kind === "refused";

/**
 * Si può confermare la sfida sui codici già usati?
 *
 * Diversa da `canSubmit`, e non per sfumatura: le due governano domande
 * opposte. Il pulsante di avvio deve tacere mentre una sfida è aperta, perché
 * avviare di nuovo sarebbe un secondo intento; il pulsante della sfida invece
 * serve **proprio** in quello stato, ed è l'unico in cui ha senso premerlo.
 * Con una sola funzione per entrambi, uno dei due restava muto: era la sfida.
 */
export const canConfirm = (outcome: StartOutcome): boolean =>
  outcome.kind === "idle" ||
  outcome.kind === "refused" ||
  outcome.kind === "needs_confirmation";

/**
 * Cosa fare dopo un rifiuto.
 *
 * `CONFIRMATION_STALE` è l'unico che riapre un dialogo: il consenso era per
 * un'analisi scaduta, e riusarlo sarebbe un consenso per qualcosa che l'utente
 * non ha visto.
 *
 * `CUSTOM_FIELDS_UNAVAILABLE` **non** deve ripiegare sull'avvio legacy: il
 * servizio indisponibile è esattamente il caso in cui il fallback scriverebbe
 * senza autorizzazione. Si dice che non si può, e si aspetta.
 */
export function reactionTo(code: string): {
  reopenConfirmation: boolean;
  retryable: boolean;
  fallbackToLegacy: boolean;
} {
  switch (code) {
    case "CONFIRMATION_STALE":
      return { reopenConfirmation: true, retryable: true, fallbackToLegacy: false };
    case "DUPLICATE_CONFIRMATION_REQUIRED":
      return { reopenConfirmation: true, retryable: true, fallbackToLegacy: false };
    case "REVISION_CONFLICT":
      // Lo stato è cambiato sotto: si rilegge e si riprepara, con lo stesso id.
      return { reopenConfirmation: false, retryable: true, fallbackToLegacy: false };
    case "OPERATION_ID_CONFLICT":
      // Lo stesso id con un payload diverso. Non è ritentabile: è un secondo
      // intento travestito da retry.
      return { reopenConfirmation: false, retryable: false, fallbackToLegacy: false };
    case "CUSTOM_FIELDS_UNAVAILABLE":
      return { reopenConfirmation: false, retryable: true, fallbackToLegacy: false };
    default:
      return { reopenConfirmation: false, retryable: false, fallbackToLegacy: false };
  }
}

/**
 * Un'origine automatica può chiedere una conferma a un umano?
 *
 * No. Un timer o un robot non hanno nessuno davanti: un rifiuto per essi è una
 * notifica con una correzione da fare, non un dialogo che nessuno aprirà. Il
 * piano lo dice come «nessun tasto approva e nessun pending amministrativo».
 */
export const canAskHuman = (origin: OperationOrigin): boolean =>
  origin === "interactive";

/**
 * La richiesta è scaduta senza risposta?
 *
 * Un timeout è l'unico esito in cui **non si sa** se il motore ha avviato: la
 * richiesta può essere arrivata e la risposta essersi persa. Trattarlo come un
 * rifiuto sarebbe una bugia in una direzione, ritentarlo come un intento nuovo
 * sarebbe una bugia nell'altra — quindi si distingue, e si va a leggere l'esito.
 *
 * `ECONNABORTED` è il codice di axios per il timeout; il confronto sul messaggio
 * copre i client che non lo impostano.
 */
export const isTimeout = (error: unknown): boolean => {
  const e = error as { code?: string; message?: string } | null;
  return e?.code === "ECONNABORTED" || /timeout/i.test(e?.message ?? "");
};

/**
 * Il testo dei codici già usati, per il dialogo di duplicato.
 *
 * I codici sono **dati**, non messaggi: vengono mostrati come sono. Comporre una
 * frase qui significherebbe una seconda traduzione, e quella autorevole è nel
 * catalogo.
 *
 * Due nomi per la stessa cosa, e non è un'ambiguità del client: l'**anteprima**
 * dell'operazione porta `codes` (il risultato preparato), mentre la **sfida**
 * del 409 porta `candidateCodes` in `error.params` — i codici che qualcun altro
 * ha emesso e che collidono. Si leggono entrambi perché il dialogo mostra i
 * secondi e l'esito mostra i primi.
 */
export function duplicateCodesFrom(
  payload: Record<string, unknown> | null | undefined
): string[] {
  const codes = payload?.codes ?? payload?.candidateCodes;
  return Array.isArray(codes) ? codes.map(String) : [];
}
